import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseUrls } from '@app/app.constants';
import { Announcement } from '@app/core/interfaces/c5/announcement';
import { AnnouncementType } from '@app/core/interfaces/c5/announcement-type';
import { AnnouncementUpdate } from '@app/shared/acd-cc/models/announcement-update';
import { AcdUtilsService } from '@app/shared/acd-cc/utils/acd-utils.service';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { AnnouncementSettings } from '@business/acd/announcement/models/announcement-settings';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { UserLevel } from '../models/user-level';

@Injectable({
  providedIn: 'root',
})
export class AcdAnnouncementRepository {
  constructor(
    private acdUtilsService: AcdUtilsService,
    private http: HttpClient,
    private utils: Utils
  ) {}

  add(
    queueId: number,
    description: string,
    file: File,
    type: AnnouncementType = AnnouncementType.ACD
  ): Observable<void> {
    const formData = new FormData();

    formData.append('description', description);
    formData.append('file', file, file.name);

    // return this.utils.apiUrl(`/acdQueue/${queueId}/announcement`).pipe(
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]:
          BaseUrls.ServiceProvider.Enterprise + `/acdQueue/${queueId}/announcement`,
        [UserLevel.EnterpriseAdmin]: BaseUrls.Enterprise.Enterprise + `/acdQueue/${queueId}/announcement`,
        [UserLevel.EndUser]: `/user/acd/queue/${queueId}/announcement`,
      })
      .pipe(
        mergeMap((url) =>
          this.http.post<void>(url, formData, {
            params: {
              type,
            },
          })
        ),
        wrapError('acd-announcement-repository:add')
      );
  }

  delete(queueId: number, id: number): Observable<void> {
    // return this.utils.apiUrl(`/acdQueue/${queueId}/announcement/${id}`).pipe(
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]:
          BaseUrls.ServiceProvider.Enterprise + `/acdQueue/${queueId}/announcement/${id}`,
        [UserLevel.EnterpriseAdmin]:
          BaseUrls.Enterprise.Enterprise + `/acdQueue/${queueId}/announcement/${id}`,
        [UserLevel.EndUser]: `/user/acd/queue/${queueId}/announcement/${id}`,
      })
      .pipe(
        mergeMap((url) => this.http.delete<void>(url)),
        wrapError('acd-announcement-repository:delete')
      );
  }

  get(queueId: number, id: string): Observable<Announcement> {
    return this.utils.apiUrl(`/acdQueue/${queueId}/announcement/${id}`).pipe(
      mergeMap((url) => this.http.get<Announcement>(url)),
      wrapError('acd-announcement-repository:get')
    );
  }

  list(queueId: number, domainAnnouncementsToo: boolean = true): Observable<Announcement[]> {
    return this.acdUtilsService.fetchAnnouncements(queueId, domainAnnouncementsToo);
  }

  update(queueId: number, announcement: AnnouncementUpdate, file?: File): Observable<void> {
    const formData = new FormData();

    formData.append('announcement', JSON.stringify(announcement));

    if (file != null) {
      formData.append('file', file, file.name);
    }

    // return this.utils.apiUrl(`/acdQueue/${queueId}/announcement/${announcement.id}`).pipe(
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]:
          BaseUrls.ServiceProvider.Enterprise + `/acdQueue/${queueId}/announcement/${announcement.id}`,
        [UserLevel.EnterpriseAdmin]:
          BaseUrls.Enterprise.Enterprise + `/acdQueue/${queueId}/announcement/${announcement.id}`,
        [UserLevel.EndUser]: `/user/acd/queue/${queueId}/announcement/${announcement.id}`,
      })
      .pipe(
        mergeMap((url) => this.http.put<void>(url, formData)),
        wrapError('acd-announcement-repository:update')
      );
  }

  updateSettings(queueId: number, settings: AnnouncementSettings): Observable<void> {
    // return this.utils.apiUrl(`/acdQueue/${queueId}/announcements/settings`).pipe(
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]:
          BaseUrls.ServiceProvider.Enterprise + `/acdQueue/${queueId}/announcements/settings`,
        [UserLevel.EnterpriseAdmin]:
          BaseUrls.Enterprise.Enterprise + `/acdQueue/${queueId}/announcements/settings`,
        [UserLevel.EndUser]: `/user/acd/queue/${queueId}/announcements/settings`,
      })
      .pipe(
        mergeMap((url) => this.http.put<void>(url, settings)),
        wrapError('acd-announcement-repository:update-settings')
      );
  }
}
